<template>
  <fieldset>
    <InputPatternEditor
      :value="state.pattern"
      :showType="isTypeConfigRequired"
      v-if="isVisible('pattern')"
      @updated="patternUpdated"
    />

    <div v-if="!allowsAnyValue">
      <div class="field is-horizontal" v-if="isVisible('allowedAttempts')">
        <div class="field-label">
          <label class="label">
            Max Attempts
            <n-tooltip
              position="top"
              text="The number of attempts allowed for the inputted value to meet the input requirements. If the limit of attempts is reached, the flow will transition into the 'Max Attempts Exceeded' path"
            />
          </label>
        </div>
        <div class="field-body">
          <div class="control">
            <div class="select">
              <select :id="allowedAttemptsId" v-model="allowedAttempts" @change="allowedAttemptsUpdated">
                <option :value="-1">Unlimited</option>
                <option v-for="index in 5" :key="index">{{ index }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      <div class="field is-horizontal" v-if="isVisible('errorMessage') && state.allowedAttempts !== 1">
        <div class="field-label">
          <label class="label">
            Error Message
            <n-tooltip
              position="top"
              text="Message to play when a value was entered that doesn\'t meet the input requirements. If this is blank, the original message will be repeated"
            />
          </label>
        </div>
        <div class="field-body">
          <div class="control">
            <textarea
              class="textarea"
              placeholder="Prompt the user to try again"
              type="text"
              v-model.trim="state.errorMessage"
              @change="configChanged"
            >
            </textarea>
          </div>
        </div>
      </div>

      <div class="field is-horizontal" v-if="isVisible('noMatch') && state.allowedAttempts !== -1">
        <div class="field-label">
          <label class="label">
            After Max Attempts
            <n-tooltip
              position="top"
              text="Transition to another path if a value was entered that doesn't meet the input requirements"
            />
          </label>
        </div>
        <div class="field-body">
          <div class="control">
            <TransitionPicker :transition="state.noMatch" @transition-selected="noMatchUpdated" />
            <p v-if="isTransitionPayloadValidationInvalid" class="help is-danger">
              Must not contain an invalid transition
            </p>
            <p v-else-if="isTransitionValidationInvalid" class="help is-danger">Must select a transition</p>
          </div>
        </div>
      </div>
    </div>
  </fieldset>
</template>

<script lang="ts" setup>
import { ref, computed, watch, PropType } from 'vue';
import useVuelidate from '@vuelidate/core';
import { required, minLength, requiredIf } from '@vuelidate/validators';
import { StateViewModel } from '@/features/bots/models/designer';
import InputPatternEditor from './InputPatternEditor.vue';
import TransitionPicker from '@/features/bots/components/designer/TransitionPicker.vue';
import { TransitionViewModel } from '@/features/bots/models/designer/transitionViewModel';
import { TransitionType } from '@/features/bots/models/designer/transitionType';
import { useStore } from 'vuex';
import VuelidateUtil from '@shared/util/vuelidateUtil';
import { ConditionOperationType } from '@shared/components/conditions/models/conditions';

const props = defineProps({
  value: {
    type: Object as PropType<StateViewModel>,
    required: true,
  },
  fields: {
    type: Array as PropType<string[]>,
    default: [],
  },
});

const emit = defineEmits<{
  (e: 'updated', state: StateViewModel, invalid: boolean): void;
}>();

const store = useStore();
const isTransitionPayloadInvalid = (payload: string) =>
  computed(() => store.getters.isTransitionPayloadInvalid(payload));

const state = ref<StateViewModel>({
  id: '',
  component: 'InputString',
});

const noMatch = ref({});
const allowedAttempts = ref(-1);
const allowsAnyValue = ref(true);
const invalidPattern = ref(false);

const allowedAttemptsId = computed(() => `allowed-attempts-${state.value.id}`);
const isTypeConfigRequired = computed(() => {
  return !isInputForCustomAttribute() || isInputCustomAttributeDataType('text');
});

const validations = {
  state: {
    id: { required },
    component: { required },
    pattern: {
      required: requiredIf(() => isVisible('pattern')),
      minLength: minLength(1),
    },
    noMatch: {
      payload: {
        payloadValid(payload: string) {
          return !payload || !isTransitionPayloadInvalid(payload).value;
        },
      },
    },
  },
};

const $v = useVuelidate(validations, { state });

const isTransitionValidationInvalid = computed(() => VuelidateUtil.isValidationInvalid($v, (v) => v.state.noMatch));
const isTransitionPayloadValidationInvalid = computed(() =>
  VuelidateUtil.isValidationInvalid($v, (v) => v.state.noMatch.payload, 'payloadValid'),
);

watch(
  () => props.value,
  (newValue: StateViewModel) => {
    state.value = newValue;
    refreshComponent();
  },
  { immediate: true },
);

watch(
  () => props.fields,
  () => {
    refreshComponent();
    configChanged();
  },
);

function noMatchUpdated(action: TransitionViewModel) {
  noMatch.value = action;
  state.value.noMatch = action;
  configChanged();
}

function patternUpdated(pattern: string, invalid: boolean) {
  let hasPatternChanged = false;

  if (state.value.pattern !== pattern || invalidPattern.value !== invalid) {
    hasPatternChanged = true;
  }

  state.value.pattern = pattern;
  invalidPattern.value = invalid;
  allowsAnyValue.value = allowsAnyPattern();

  if (allowsAnyValue.value) {
    clearMatchingOptions();
  }

  if (hasPatternChanged) {
    configChanged();
  }
}

function allowedAttemptsUpdated() {
  state.value.allowedAttempts = Number(allowedAttempts.value);
  configChanged();
}

function configChanged() {
  $v.value.$touch();
  emit('updated', state.value, invalidPattern.value || $v.value.$invalid);
}

function isVisible(key: string) {
  return props.fields && props.fields.includes(key);
}

function allowsAnyPattern() {
  return state.value.pattern === '^.+$';
}

function clearMatchingOptions() {
  allowedAttempts.value = -1;
  noMatch.value = '';
  state.value.allowedAttempts = -1;
  state.value.noMatch = { type: TransitionType.Path, payload: '' };
  state.value.errorMessage = '';
}

function refreshComponent() {
  if (state.value.noMatch) {
    noMatch.value = state.value.noMatch;
  }

  if (state.value.allowedAttempts !== null && state.value.allowedAttempts !== undefined) {
    allowedAttempts.value = state.value.allowedAttempts;
  }

  allowsAnyValue.value = allowsAnyPattern();

  if ($v.value.$invalid) {
    configChanged();
  }
}

function isInputForCustomAttribute() {
  return state.value.storedVariable?.scope === 'appUser.customAttributes';
}

function isInputCustomAttributeDataType(dataType: string) {
  return state.value.storedVariable?.dataType === dataType;
}
</script>

<style scoped lang="scss">
fieldset {
  :deep(.field-body) {
    flex: 2;

    .control {
      flex: 1;
    }
  }
}

textarea {
  resize: none;
}
</style>
